import React, { useEffect, useImperativeHandle, forwardRef, useMemo, useState } from 'react'
import { View, StyleSheet } from 'react-native'
import Svg, { Circle } from 'react-native-svg'
import Animated, {
  useSharedValue,
  withTiming,
  useAnimatedProps,
  Easing,
  cancelAnimation,
  runOnJS,
  interpolateColor,
  useAnimatedReaction
} from 'react-native-reanimated'
import { Platform } from 'react-native'

const isWeb = Platform.OS === 'web'
const EPS = 0.0001 // avoids rounding edge cases with round caps

type Props = {
  duration: number

  initialProgress?: number

  size?: number
  strokeWidth?: number

  /** Single color (fallback if colors not provided) */
  color?: string

  /** Array of colors to change over time (start -> end) */
  colors?: string[]

  /** When using `colors`, choose step or smooth color transitions */
  colorMode?: 'step' | 'interpolate'

  /** Where each color starts (0..1), same length as `colors`. e.g. [0, 0.75] */
  colorStops?: number[]

  trackColor?: string
  clockwise?: boolean
  autoStart?: boolean
  countdown?: boolean
  onComplete?: () => void
  children?: React.ReactNode
}

export type CircularTimerProgressRef = {
  start: () => void
  pause: () => void
  resume: () => void
  reset: () => void
  setProgress: (p: number) => void // 0..1
  getRemaining: () => number // ms
}

const AnimatedCircle = Animated.createAnimatedComponent(Circle)

const CircularTimerProgress = forwardRef<CircularTimerProgressRef, Props>(
  (
    {
      duration,
      size = 140,
      strokeWidth = 10,
      color = '#4F46E5',
      colors,
      colorMode = 'step',
      colorStops,
      trackColor = 'rgba(0,0,0,0.1)',
      clockwise = true,
      autoStart = true,
      countdown = true,
      onComplete,
      children,
      initialProgress = 0
    },
    ref
  ) => {
    const r = useMemo(() => (size - strokeWidth) / 2, [size, strokeWidth])
    const circumference = useMemo(() => 2 * Math.PI * r, [r])

    if (initialProgress < 0 || initialProgress > 1) {
      console.warn('CircularTimerProgress: Props mismatch initialProgress should be between 0 and 1')
    }
    const progress = useSharedValue(initialProgress) // 0 -> 1 (elapsed)
    const remainingMs = useSharedValue(duration)
    const lastDurationMs = useSharedValue(duration)
    const running = useSharedValue(false)

    const hasColors = Array.isArray(colors) && colors.length > 0
    const stops = hasColors ? (colors as string[]) : [color]
    if (colorStops && colorMode === 'step' && colorStops.length !== colors?.length) {
      console.warn('CircularTimerProgress: Props mismatch colorStops should have the same length as colors')
    }

    const computeStrokeColor = (tElapsed: number) => {
      if (stops.length === 1) return stops[0]
      if (colorMode === 'interpolate') {
        const n = stops.length
        const input = Array.from({ length: n }, (_, i) => (n === 1 ? 0 : i / (n - 1)))
        return interpolateColor(tElapsed, input as any, stops as any) as unknown as string
      }
      // step mode
      let idx = 0
      for (let i = 0; i < positions.length; i++) {
        if (tElapsed >= positions[i]) idx = i
        else break
      }
      return stops[idx]
    }

    const positions = useMemo(() => {
      if (Array.isArray(colorStops) && colorStops.length === stops.length) {
        // clamp 0..1 and ensure ascending
        const clamped = colorStops.map((v) => Math.min(1, Math.max(0, v)))
        return clamped.slice().sort((a, b) => a - b)
      }
      if (stops.length === 1) return [0]
      return stops.map((_, i) => (stops.length === 1 ? 0 : i / (stops.length - 1)))
    }, [colorStops, stops])

    /**
     * WEB FALLBACK: drive regular React props
     * --------------------------------------- */
    const [webStroke, setWebStroke] = useState({
      dasharray: `${circumference}`,
      dashoffset: 0,
      stroke: stops[0]
    })

    const updateWeb = (tElapsed: number) => {
      const t = countdown ? 1 - tElapsed : tElapsed // 0..1 visible arc
      const arc = Math.max(0, Math.min(1, t)) * (circumference - EPS)
      setWebStroke((prev) => {
        const stroke = computeStrokeColor(tElapsed)
        const dasharray = `${arc}, ${Math.max(0, circumference - arc)}`
        if (prev.dasharray === dasharray && prev.stroke === stroke) return prev
        return { dasharray, dashoffset: 0, stroke }
      })
    }

    // react to progress on UI and push to JS on web
    useAnimatedReaction(
      () => progress.value,
      (t) => {
        if (isWeb) runOnJS(updateWeb)(t)
      }
    )

    const startInternal = () => {
      const ms = remainingMs.value
      lastDurationMs.value = ms
      running.value = true
      progress.value = withTiming(1, { duration: ms, easing: Easing.linear }, (finished) => {
        if (finished) {
          running.value = false
          remainingMs.value = 0
          if (onComplete) runOnJS(onComplete)()
        }
      })
    }

    const pauseInternal = () => {
      if (!running.value) return
      cancelAnimation(progress)
      const remaining = Math.max(0, (1 - progress.value) * lastDurationMs.value)
      remainingMs.value = remaining
      running.value = false
    }

    const resumeInternal = () => {
      if (running.value || remainingMs.value <= 0) return
      startInternal()
    }

    const resetInternal = () => {
      cancelAnimation(progress)
      progress.value = 0
      remainingMs.value = duration
      lastDurationMs.value = duration
      running.value = false
    }

    const setProgressInternal = (p: number) => {
      cancelAnimation(progress)
      const clamped = Math.max(0, Math.min(1, p))
      progress.value = clamped
      remainingMs.value = Math.max(0, (1 - clamped) * duration)
      running.value = false
    }

    useImperativeHandle(ref, () => ({
      start: startInternal,
      pause: pauseInternal,
      resume: resumeInternal,
      reset: resetInternal,
      setProgress: setProgressInternal,
      getRemaining: () => Math.max(0, Math.round(remainingMs.value))
    }))

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      resetInternal()
      if (autoStart) startInternal()
    }, [duration])

    // mirror to shared values so the UI thread sees updates
    const stopsSV = useSharedValue(stops)
    const positionsSV = useSharedValue(positions)

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      stopsSV.value = stops
    }, [stops])
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      positionsSV.value = positions
    }, [positions])

    /**
     * NATIVE PATH: useAnimatedProps (OK on iOS/Android)
     * --------------------------------------- */
    const animatedProps = useAnimatedProps(() => {
      if (isWeb) {
        return {}
      }
      const tElapsed = progress.value
      const t = countdown ? 1 - tElapsed : tElapsed
      const arc = Math.max(0, Math.min(1, t)) * (circumference - EPS)
      let stroke = stops[0]
      if (stops.length > 1) {
        if (colorMode === 'interpolate') {
          const n = stops.length
          const input = Array.from({ length: n }, (_, i) => (n === 1 ? 0 : i / (n - 1)))
          stroke = interpolateColor(tElapsed, input as any, stops as any)
        } else {
          // step (with custom positions if you have them)
          const pos = positions ?? stops.map((_, i) => (stops.length === 1 ? 0 : i / (stops.length - 1)))
          let idx = 0
          for (let i = 0; i < pos.length; i++) {
            if (tElapsed >= pos[i]) idx = i
            else break
          }
          stroke = stops[idx]
        }
      }

      return {
        strokeDasharray: [arc, Math.max(0, circumference - arc)],
        strokeDashoffset: 0,
        stroke
      } as any
    }, [circumference, countdown, positions, stops, colorMode])

    const animatedCircleProps = isWeb
      ? {
          stroke: webStroke.stroke,
          strokeDasharray: webStroke.dasharray,
          strokeDashoffset: webStroke.dashoffset
        }
      : {
          animatedProps,
          originX: size / 2,
          originY: size / 2
        }

    return (
      <View style={{ width: size, height: size }}>
        <View
          style={[
            StyleSheet.absoluteFill,
            {
              transform: [{ scaleX: clockwise ? 1 : -1 }]
            }
          ]}
        >
          <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <Circle cx={size / 2} cy={size / 2} r={r} stroke={trackColor} strokeWidth={strokeWidth} fill="none" />
            <AnimatedCircle
              cx={size / 2}
              cy={size / 2}
              r={r}
              strokeWidth={strokeWidth}
              strokeLinecap="butt"
              fill="none"
              {...animatedCircleProps}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          </Svg>
        </View>
        <View style={styles.center}>{children}</View>
      </View>
    )
  }
)

const styles = StyleSheet.create({
  center: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center'
  }
})

export default CircularTimerProgress
